/**
 * FiltersModal Component
 *
 * Modal with sidebar navigation for filter categories (Format, Status, Catalogues, Categories, Tags).
 * Layout only - filter body content is built in separate issues (GRAIN-1345-1351).
 */

// React
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// UI primitives
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';

// Animation
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

// Hooks and utilities
import { useTranslation } from '../../../hooks/useTranslation';
import { cn } from '@/lib/utils';
import { UNDERLINE_SLIDE_IN_GROUP } from '@/components/ui/link-underline';

// Types
import { FILTER_CATEGORIES } from '../types';
import type { FiltersModalProps, FilterCategory } from '../types';

/* ---- Layout constants ---- */
const MODAL_HEIGHT = 822;
const SIDEBAR_WIDTH = 'w-60';
const FOCUS_DELAY_MS = 100;

/* ---- Animation constants ---- */
const SLIDE_DISTANCE_PX = 14;
const SCALE_BUMP = [1, 1.15, 1];
const SCALE_DURATION_S = 0.25;
const SLIDE_DURATION_S = 0.2;
const BLUR_SWAP_DURATION_S = 0.15;

/* ---- Scrollbar classes (extends multi-select-dropdown pattern) ---- */
const BODY_SCROLLBAR_CLASSES = [
  '[&::-webkit-scrollbar]:w-6',
  '[&::-webkit-scrollbar-track]:bg-off-white',
  '[&::-webkit-scrollbar-thumb]:bg-dinnerplate-white',
  '[&::-webkit-scrollbar-thumb]:rounded-full',
  '[&::-webkit-scrollbar-thumb]:border-[6px]',
  '[&::-webkit-scrollbar-thumb]:border-solid',
  '[&::-webkit-scrollbar-thumb]:border-off-white',
].join(' ');

/* ---- Shared class constants ---- */
const SHORTCUT_KEY_CLASSES = 'inline-flex items-center justify-center size-8 border border-dinnerplate-white rounded-xs font-inter text-xs leading-[1.25] text-grey';

// Fixed height, not padding: the count badge is taller than the label, so a
// content-sized row grew the moment a filter was picked and shifted every row
// below it.
const SIDEBAR_ITEM_BASE = 'w-full h-15 flex items-center justify-between rounded-input px-4 text-left font-inter text-sm transition-[color,background-color,transform] outline-none';
const SIDEBAR_ITEM_ACTIVE = `${SIDEBAR_ITEM_BASE} bg-dinnerplate-white-light font-bold text-off-black`;
const SIDEBAR_ITEM_INACTIVE = `${SIDEBAR_ITEM_BASE} font-semibold text-grey hover:bg-dinnerplate-white-light/50 hover:text-off-black motion-safe:active:scale-95`;

const SIDEBAR_BADGE_CLASSES = 'inline-flex items-center justify-center size-7 py-1 px-2 rounded-xs bg-grey-light font-inter text-xs font-semibold leading-tight-sm text-grey tabular-nums overflow-hidden';

/* ---- Skeleton constants ---- */
const SKELETON_SECTIONS = [
  { labelWidth: 'w-32', chips: 5 },
  { labelWidth: 'w-24', chips: 4 },
  { labelWidth: 'w-20', chips: 3 },
];
const SKELETON_CHIP_WIDTHS = ['w-20', 'w-24', 'w-16', 'w-28', 'w-20'];

/* ---- Sub-components ---- */

interface FilterSidebarItemProps {
  category: FilterCategory;
  label: string;
  isActive: boolean;
  count?: number;
  onClick: () => void;
}

function FilterSidebarItem({ category, label, isActive, count, onClick }: FilterSidebarItemProps) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={`filter-panel-${category}`}
      tabIndex={isActive ? 0 : -1}
      className={isActive ? SIDEBAR_ITEM_ACTIVE : SIDEBAR_ITEM_INACTIVE}
      onClick={onClick}
    >
      <span>{label}</span>
      {count != null && count > 0 ? (
        <AnimatedCount count={count} className={SIDEBAR_BADGE_CLASSES} />
      ) : null}
    </button>
  );
}

interface FiltersFooterProps {
  selectedCount: number;
  modifierKey: string;
  selectAllLabel: string;
  clearAllLabel: string;
  applyLabel: string;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  onApply: () => void;
}

function FiltersFooter({ selectedCount, modifierKey, selectAllLabel, clearAllLabel, applyLabel, onSelectAll, onClearAll, onApply }: FiltersFooterProps) {
  const hasSelection = selectedCount > 0;

  return (
    <div className="flex w-full items-center justify-between" role="status" aria-live="polite">
      <button
        type="button"
        className="group/underline flex items-center gap-4"
        onClick={hasSelection ? onClearAll : onSelectAll}
      >
        <div className="flex items-center gap-2">
          <span className={SHORTCUT_KEY_CLASSES}>{modifierKey}</span>
          <span className={SHORTCUT_KEY_CLASSES}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={hasSelection ? 'C' : 'A'}
                initial={{ opacity: 0, filter: 'blur(4px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(4px)' }}
                transition={{ duration: BLUR_SWAP_DURATION_S, ease: 'easeOut' }}
              >
                {hasSelection ? 'C' : 'A'}
              </motion.span>
            </AnimatePresence>
          </span>
        </div>
        <span className={cn(UNDERLINE_SLIDE_IN_GROUP, 'font-inter text-sm font-semibold text-grey group-hover/underline:text-off-black')}>
          {hasSelection ? clearAllLabel : selectAllLabel}
        </span>
      </button>

      <Button
        className="transition-[background-color,color,transform] duration-300 motion-safe:active:scale-95"
        onClick={onApply}
      >
        {applyLabel}
      </Button>
    </div>
  );
}

/** Animated count badge - slides numbers up/down with a scale bump on change. */
function AnimatedCount({ count, className }: { count: number; className: string }) {
  const prevCountRef = useRef(count);
  const reducedMotion = useReducedMotion();
  const direction = count > prevCountRef.current ? 1 : -1;
  prevCountRef.current = count;

  if (reducedMotion) {
    return <span className={className}>{count}</span>;
  }

  return (
    <motion.span
      key={count}
      className={className}
      initial={{ scale: 1 }}
      animate={{ scale: SCALE_BUMP }}
      transition={{ duration: SCALE_DURATION_S, ease: 'easeOut' }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={count}
          initial={{ y: direction * SLIDE_DISTANCE_PX, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: direction * -SLIDE_DISTANCE_PX, opacity: 0 }}
          transition={{ duration: SLIDE_DURATION_S, ease: 'easeOut' }}
        >
          {count}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}

function FiltersSkeleton() {
  return (
    <div className="flex flex-col gap-5 animate-pulse motion-reduce:animate-none">
      {SKELETON_SECTIONS.map((section, i) => (
        <div key={i}>
          <div className={cn('h-3 bg-dinnerplate-white rounded mb-3', section.labelWidth)} />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: section.chips }, (_, j) => (
              <div key={j} className={cn('h-8 bg-dinnerplate-white rounded-lg', SKELETON_CHIP_WIDTHS[j % SKELETON_CHIP_WIDTHS.length])} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---- Main component ---- */

export function FiltersModal({ open, onOpenChange, filters, children, categoryCounts, isLoading }: FiltersModalProps) {
  const { t } = useTranslation();
  const [internalCategory, setInternalCategory] = useState<FilterCategory>('format');
  const activeCategory = filters?.activeCategory ?? internalCategory;
  const setActiveCategory = filters?.setActiveCategory ?? setInternalCategory;
  const resolvedCategoryCounts = filters?.categoryCounts ?? categoryCounts;
  const resolvedIsLoading = filters?.isLoading ?? isLoading;
  const modifierKey = useMemo(() => /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌥' : 'Alt', []);
  const sidebarRef = useRef<HTMLElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const bodyRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    observerRef.current = new ResizeObserver(() => {
      setIsScrollable(node.scrollHeight > node.clientHeight);
    });
    observerRef.current.observe(node);
    setIsScrollable(node.scrollHeight > node.clientHeight);
  }, []);

  const handleClose = useCallback(() => {
    filters?.reset();
    onOpenChange(false);
  }, [onOpenChange, filters]);

  const handleApply = useCallback(() => {
    filters?.apply();
    onOpenChange(false);
  }, [onOpenChange, filters]);

  // Keyboard navigation for sidebar
  const handleSidebarKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = sidebarRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    if (!items?.length) return;

    const currentIndex = FILTER_CATEGORIES.indexOf(activeCategory);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % items.length;
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = items.length - 1;
    } else {
      return;
    }

    setActiveCategory(FILTER_CATEGORIES[nextIndex]);
    items[nextIndex].focus();
  }, [activeCategory]);

  // Keyboard shortcuts: Option/Alt + A (select all), Option/Alt + C (clear all)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return;

      if (e.key === 'a' || e.key === 'å') {
        e.preventDefault();
        filters?.selectAll?.();
      } else if (e.key === 'c' || e.key === 'ç') {
        e.preventDefault();
        filters?.clearAll?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, filters?.selectAll, filters?.clearAll]);

  // Focus active sidebar item when modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        sidebarRef.current?.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')?.focus();
      }, FOCUS_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const footer = (
    <FiltersFooter
      selectedCount={resolvedCategoryCounts?.[activeCategory] ?? 0}
      modifierKey={modifierKey}
      selectAllLabel={`${t('filters.select_all')} ${t(`filters.${activeCategory}_plural`).toLowerCase()}`}
      clearAllLabel={`${t('filters.clear_all')} ${t(`filters.${activeCategory}_plural`).toLowerCase()}`}
      applyLabel={t('filters.apply')}
      onSelectAll={filters?.selectAll}
      onClearAll={filters?.clearAll}
      onApply={handleApply}
    />
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={handleClose}
        aria-label={t('filters.title')}
        className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
        style={{ height: MODAL_HEIGHT }}
      >
        <ModalLayout
          title={<ModalTitle>{t('filters.title')}</ModalTitle>}
          onClose={handleClose}
          closeLabel={t('filters.close')}
          footer={footer}
          contentClassName="p-0 overflow-hidden flex flex-col"
        >
          <div className="flex flex-1 min-h-0">
            {/* Sidebar */}
            <nav
              ref={sidebarRef}
              role="tablist"
              aria-label={t('filters.categories_label')}
              aria-orientation="vertical"
              className={cn('flex shrink-0 flex-col gap-4 p-8', SIDEBAR_WIDTH)}
              onKeyDown={handleSidebarKeyDown}
            >
              {FILTER_CATEGORIES.map((category) => (
                <FilterSidebarItem
                  key={category}
                  category={category}
                  label={t(`filters.${category}`)}
                  isActive={activeCategory === category}
                  count={resolvedCategoryCounts?.[category]}
                  onClick={() => setActiveCategory(category)}
                />
              ))}
            </nav>

            {/* Divider */}
            <div className="w-px bg-dinnerplate-white" />

            {/* Body - content built in GRAIN-1345-1351 */}
            <div className="relative flex flex-col flex-1 min-h-0">
              <div
                ref={bodyRef}
                role="tabpanel"
                id={`filter-panel-${activeCategory}`}
                aria-label={t(`filters.${activeCategory}`)}
                tabIndex={0}
                className={cn('flex-1 min-h-0 overflow-y-auto overscroll-contain p-8 outline-none', BODY_SCROLLBAR_CLASSES)}
              >
                {resolvedIsLoading ? <FiltersSkeleton /> : children}
              </div>
              {isScrollable ? (
                <div className="absolute right-6 top-0 bottom-0 w-px bg-dinnerplate-white pointer-events-none" />
              ) : null}
            </div>
          </div>
        </ModalLayout>
      </DialogContent>
    </Dialog>
  );
}
